import React from 'react';
import ImageCarousel from './ImageCarousel';
import { formatCurrency, formatNumber, formatPropertyStyle } from '../utils/formatters';
import { INVESTMENT_TYPE_COLORS } from '../config/filters';
import useSaveProperty from '../hooks/useSaveProperty';
import { useComparison } from '../context/ComparisonContext';
import { useToast } from '../context/ToastContext';
import { Heart } from 'lucide-react';
import './PropertyCard.css';

const PropertyCard = ({ property, onCardClick, isSaved: isSavedProp = false, onSaveToggle }) => {

    const { saved, saving, toggleSave } = useSaveProperty(
        property.property_id, { initialSaved: isSavedProp, onToggle: onSaveToggle }
    );

    const { addToCompare, removeFromCompare, isInCompare, compareCount } = useComparison();
    const { showToast } = useToast();
    const inCompare = isInCompare(property.property_id);

    const handleCompareToggle = (e) => {
        e.stopPropagation();
        if (inCompare) {
            removeFromCompare(property.property_id);
        } else {
            if (compareCount >= 5) {
                showToast('Maximum 5 properties for comparison', 'warning');
                return;
            }
            addToCompare({
                property_id: property.property_id,
                address: property.address, city: property.city, state: property.state,
                list_price: property.list_price, beds: property.beds, baths: property.baths,
                sqft: property.sqft, primary_photo_url: property.primary_photo_url,
                arv: property.arv, estimated_rent: property.estimated_rent,
                roi_percentage: property.roi_percentage
            });
        }
    };

    // Prepare images for carousel
    const images = property.photos && property.photos.length > 0
        ? property.photos
        : (property.primary_photo_url ? [property.primary_photo_url] : []);

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onCardClick(property);
        }
    };

    const getInfoChip = () => {
        if (property.list_date) {
            const days = Math.floor((Date.now() - new Date(property.list_date).getTime()) / 86400000);
            return `${days} days on RevestAI`;
        }
        if (property.style) {
            return formatPropertyStyle(property.style);
        }
        return null;
    };

    const infoChipText = getInfoChip();

    // Get investor tags for display (LLM returns them pre-sorted by priority)
    const getInvestorTags = () => {
        const tags = property.analysis?.investor_tags || [];
        if (tags.length === 0) return { visible: [], overflow: 0 };

        return {
            visible: tags.slice(0, 3),
            overflow: Math.max(0, tags.length - 3)
        };
    };

    const { visible: visibleTags, overflow: overflowCount } = getInvestorTags();

    return (
        <div
            className="property-card"
            onClick={() => onCardClick(property)}
            onKeyPress={handleKeyPress}
            role="button"
            tabIndex={0}
            aria-label={`Property at ${property.address}, ${formatCurrency(property.list_price)}`}
        >
            <div className="card-image-container">
                <ImageCarousel images={images} alt={`Exterior of ${property.address}`} />

                {infoChipText && (
                    <div className="info-chip">
                        {infoChipText}
                    </div>
                )}

                {/* Heart / Save Button */}
                <button
                    className={`save-heart-btn ${saved ? 'saved' : ''}`}
                    onClick={toggleSave}
                    aria-label={saved ? 'Unsave property' : 'Save property'}
                    disabled={saving}
                >
                    <Heart
                        size={22}
                        fill={saved ? '#ef4444' : 'none'}
                        stroke={saved ? '#ef4444' : 'white'}
                        strokeWidth={2}
                    />
                </button>
            </div>

            <div className="card-content">
                <div className="card-price-row">
                    <div className="card-price" aria-label={`Price: ${formatCurrency(property.list_price)}`}>
                        {formatCurrency(property.list_price)}
                    </div>
                    <button
                        className={`add-compare-btn ${inCompare ? 'in-compare' : ''}`}
                        onClick={handleCompareToggle}
                        aria-label={inCompare ? 'Remove from comparison' : 'Add to comparison'}
                    >
                        {inCompare ? '✓ In Compare' : '+ Add to Compare'}
                    </button>
                </div>
                <div className="card-details">
                    <span aria-label={`${property.beds || 0} bedrooms`}>{property.beds || '-'} bds</span>
                    <span aria-label={`${property.baths || 0} bathrooms`}>{property.baths || '-'} ba</span>
                    <span aria-label={`${formatNumber(property.sqft)} square feet`}>{formatNumber(property.sqft)} sqft</span>
                </div>
                <div className="card-address" aria-label={`Address: ${property.address}`}>
                    {property.address}
                </div>

                {/* Investment Types */}
                {property.investment_types && property.investment_types.length > 0 && (
                    <div className="investment-tags">
                        {property.investment_types.map((type, idx) => (
                            <span
                                key={idx}
                                className="investment-tag"
                                style={{ backgroundColor: INVESTMENT_TYPE_COLORS[type] || '#6b7280' }}
                            >
                                {type}
                            </span>
                        ))}
                    </div>
                )}

                {/* Investor Tags */}
                {visibleTags.length > 0 && (
                    <div className="investor-tags">
                        {visibleTags.map((tag, idx) => (
                            <span key={idx} className="investor-tag">{tag}</span>
                        ))}
                        {overflowCount > 0 && (
                            <span className="investor-tag investor-tag-overflow">+{overflowCount} more</span>
                        )}
                    </div>
                )}

                <div className="card-metrics">
                    <div className="metric">
                        <span className="label">{property.arv ? 'Est. Resale' : (property.estimated_rent ? 'Est. Rent' : 'Est. Resale')}</span>
                        <span className="value">
                            {property.arv ? formatCurrency(property.arv)
                                : (property.estimated_rent ? `${formatCurrency(property.estimated_rent)}/mo` : 'N/A')}
                        </span>
                    </div>
                    <div className="metric">
                        <span className="label">ROI</span>
                        <span className={`value ${property.roi_percentage != null ? (property.roi_percentage >= 0 ? 'positive' : 'negative') : ''}`}>
                            {property.roi_percentage != null ? property.roi_percentage.toFixed(1) + '%' : 'N/A'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(PropertyCard);
